import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { getCurrentUser, loadReport, saveReport, saveOpening } from '../api/cashReports'; // Adjust path
import Cookies from 'js-cookie'; // Import js-cookie

const initialRows = [
  { id: 1, item: '5€', quantity: 0, value: 5, total: 0 },
  { id: 2, item: '10€', quantity: 0, value: 10, total: 0 },
  { id: 3, item: '20€', quantity: 0, value: 20, total: 0 },
  { id: 4, item: '50€', quantity: 0, value: 50, total: 0 },
  { id: 5, item: '100€', quantity: 0, value: 100, total: 0 },
  { id: 13, item: '50 Cents', quantity: 0, value: 0.50, total: 0 },
  { id: 14, item: '1€ Coin', quantity: 0, value: 1, total: 0 },
  { id: 15, item: '2€ Coin', quantity: 0, value: 2, total: 0 },
];

export default function ZahlprotokollEditor({ selectedDate, setSelectedDate }) {
  const [rows, setRows] = React.useState(initialRows);
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [reportExists, setReportExists] = React.useState(false);
  const [shiftActive, setShiftActive] = React.useState(false);
  const [opening, setOpening] = React.useState(false);
  const [openingBalance, setOpeningBalance] = React.useState(0);

  // Ensure selectedDate is a Date object
  const currentDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate || Date.now());
  const dateKey = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD

  // Initialize user and load report on mount or date change
  React.useEffect(() => {
    const savedShiftState = Cookies.get('shiftActive');
    setShiftActive(savedShiftState === 'true'); // Convert string to boolean

    const initialize = async () => {
      try {
        setLoading(true);
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        if (currentUser) {
          const reportData = await loadReport(dateKey, currentUser.id);
          if (reportData) {
            setRows(reportData.data);
            setReportExists(true);
            setOpeningBalance(reportData.opening_balance); // Check if opening balance exists
            setOpening(true)
          } else {
            setRows(initialRows);
            setReportExists(false);
          }
        }
      } catch (error) {
        console.error('Initialization error:', error.message);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [dateKey]);

  const handleQuantityChange = (id, delta) => {
    const updatedRows = rows.map((row) => {
      if (row.id === id) {
        const newQuantity = Math.max(0, row.quantity + delta); // Prevent negative quantities
        return { ...row, quantity: newQuantity, total: (row.value * newQuantity).toFixed(2) };
      }
      return row;
    });
    setRows(updatedRows);
    handleSave(updatedRows); // Auto-save on change
  };

  const calculateTotalCash = () => {
    return rows.reduce((acc, row) => acc + parseFloat(row.total || 0), 0).toFixed(2);
  };

  const handleSave = async (updatedRows = rows) => {
    if (!user) {
      console.error('No user authenticated');
      return;
    }
    try {
      await saveReport(dateKey, updatedRows, user.id);
      setReportExists(true);
  
    } catch (error) {
      console.error('Save error:', error.message);
    }
  };

  const handleSaveOpening = async () => {
    if (!user) {
      console.error('No user authenticated');
      return;
    }
    try {
      const openingBalance = calculateTotalCash();
      await saveOpening(dateKey, openingBalance, user.id);
      console.log('Opening balance saved:', openingBalance);
      setOpening(true);
    } catch (error) {
      console.error('Save opening error:', error.message);
    }
  };

  const saveAsPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Zahlprotokoll", 15, 20); // Title translated to "Cash Report"
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Datum: " + currentDate.toLocaleDateString(), 15, 30); // "Date" translated to "Datum"
    doc.text("Tijd: " + currentDate.toLocaleTimeString(), 15, 38); // "Uhrzeit" translated to "Tijd"
    doc.setFontSize(10);
    doc.setLineWidth(0.5);
    doc.line(15, 42, 195, 42); // Line adjusted to be below date and time

    autoTable(doc, {
      startY: 45,
      head: [['Item', 'Aantal', 'Waarde (€)', 'Totaal (€)']], // Translated headers
      body: rows.slice(0, 5).map(row => [row.item, row.quantity, row.value, row.total]),
      theme: 'striped',
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Item', 'Aantal', 'Waarde (€)', 'Totaal (€)']], // Translated headers
      body: rows.slice(5, 8).map(row => [
        row.item,
        row.quantity,
        row.value.toFixed(2),
        row.total,
      ]),
      theme: 'striped',
    });

    const totalPaperMoney = rows.slice(0, 5).reduce((acc, row) => acc + parseFloat(row.total || 0), 0).toFixed(2);
    const totalCoins = rows.slice(5, 8).reduce((acc, row) => acc + parseFloat(row.total || 0), 0).toFixed(2);
    const totalCashInRegister = calculateTotalCash();

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Item', 'Totaal (€)']], // Translated headers
      body: [
        ['Totaal Papiergeld', totalPaperMoney], // "Total Paper Money" translated
        ['Totaal Munten', totalCoins], // "Total Coins" translated
        ['Totaal Kasgeld', totalCashInRegister], // "Total Cash in Register" translated
      ],
      theme: 'striped',
    });

    doc.line(15, doc.lastAutoTable.finalY + 10, 195, doc.lastAutoTable.finalY + 10);

    // Ensure openingBalance is initialized
    const openingBalanceValue = parseFloat(openingBalance) || 0;
    const difference = (totalCashInRegister - openingBalanceValue).toFixed(2);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Item', 'Totaal (€)']], // Translated headers
      body: [
        ['Beginsaldo', openingBalanceValue.toFixed(2)], // "Opening Balance" translated
        ['Verschil', difference], // "Difference" translated
      ],
      theme: 'striped',
    });

    const yPosition = doc.lastAutoTable.finalY + 20;

    // Add the stamp image
    const stampImage = `${process.env.PUBLIC_URL}/stamp.png`; // Path to the image in the public folder
    const imgWidth = 50; // Adjust the width of the image
    const imgHeight = 30; // Adjust the height of the image
    doc.addImage(stampImage, 'PNG', 120, yPosition, imgWidth, imgHeight);

    // Add the signature line
    doc.text("______________________", 20, yPosition + 10);
    doc.text("Handtekening", 30, yPosition + 15); // "Unterschrift" translated to "Handtekening"

    doc.save(`Zahlprotokoll-${dateKey}.pdf`); // File name translated
  };

  // Define columns with plus/minus buttons
  const columns = [
    { field: 'item', headerName: 'Item', flex: 2 },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => handleQuantityChange(params.row.id, -1)}>
            <RemoveIcon />
          </IconButton>
          <Typography>{params.value}</Typography>
          <IconButton size="small" onClick={() => handleQuantityChange(params.row.id, 1)}>
            <AddIcon />
          </IconButton>
        </Stack>
      ),
    },
    { field: 'value', headerName: 'Value (€)', flex: 1 },
    { field: 'total', headerName: 'Total (€)', flex: 1 },
  ];

  // Read-only totals columns
  const totalColumns = [
    { field: 'item', headerName: 'Item', flex: 2 },
    { field: 'quantity', headerName: 'Quantity', flex: 1, renderCell: () => '' },
    { field: 'value', headerName: 'Value (€)', flex: 1, renderCell: () => '' },
    { field: 'total', headerName: 'Total (€)', flex: 1 },
  ];

  if (loading) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography>Loading...</Typography></Box>;
  }

  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Please log in to access the cash register report.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack direction="row" spacing={2} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Cash Register Report - {currentDate.toLocaleDateString()}
        </Typography>
        {!reportExists && (
          <Button variant="outlined" color="secondary" onClick={() => handleSave()} startIcon={<AddIcon />}>
            Create Report
          </Button>
        )}
        {reportExists && !opening &&  (
          <Button variant="outlined" color="secondary" onClick={() => handleSaveOpening()} startIcon={<AddIcon />}>
            Save Starting Cash
          </Button>
        )}
        {reportExists && opening && (
        <Button
            variant="outlined"
            color="secondary"
            onClick={saveAsPDF}
            disabled={shiftActive} // Disable if shift is active
          >
            {shiftActive ? 'Schließe die Schicht, um zu exportieren' : 'Export as PDF'}
          </Button>
      )}
      </Stack>
  
      {!reportExists ? (
        <Box sx={{ marginTop: '20%', textAlign: 'center' }}>
          <Typography component="h2" variant="subtitle2" gutterBottom sx={{ fontWeight: '600' }}>
            There is no report for this date
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: '8px' }}>
            Please create a new report to get started with the cash register
          </Typography>
        </Box>
      ) : (
        <>
          <div style={{ height: 'auto', width: '100%', marginTop: '20px' }}>
            <DataGrid
              rows={rows.slice(0, 5)} // Paper money
              columns={columns}
              pageSize={10}
              disableColumnResize
              density="compact"
              hideFooter // Remove footer
            />
          </div>
  
          <div style={{ height: 'auto', width: '100%', marginTop: '20px' }}>
            <DataGrid
              rows={rows.slice(5, 8)} // Coins
              columns={columns}
              pageSize={10}
              disableColumnResize
              density="compact"
              hideFooter // Remove footer
            />
          </div>

          <Divider sx={{ pt: 2, marginTop: '20px' }} />
  
          <Stack sx={{ height: 'auto', width: '100%', pt: 3 }}>
            <DataGrid
              rows={[
                {
                  id: 1,
                  item: 'Total Paper Money',
                  quantity: '',
                  value: '',
                  total: rows.slice(0, 5).reduce((acc, row) => acc + parseFloat(row.total || 0), 0).toFixed(2),
                },
                {
                  id: 2,
                  item: 'Total Coins',
                  quantity: '',
                  value: '',
                  total: rows.slice(5, 8).reduce((acc, row) => acc + parseFloat(row.total || 0), 0).toFixed(2),
                },
                {
                  id: 3,
                  item: 'Total Cash in Register',
                  quantity: '',
                  value: '',
                  total: calculateTotalCash(),
                },
              ]}
              columns={totalColumns}
              pageSize={5}
              disableColumnResize
              density="compact"
              hideFooter // Remove footer
            />

          </Stack>
          <Stack sx={{ height: 'auto', width: '100%', marginTop: '20px' }}>
            <DataGrid
              rows={[
                {
                  id: 1,
                  item: 'Opening Balance',
                  quantity: '',
                  value: '',
                  total: openingBalance.toFixed(2),
                },
                {
                  id: 2,
                  item: 'Difference',
                  quantity: '',
                  value: '',
                  total: (
                    calculateTotalCash() - openingBalance
                  ).toFixed(2),
                },
              ]}
              columns={totalColumns}
              pageSize={5}
              disableColumnResize
              density="compact"
              hideFooter // Remove footer
            />

          </Stack>
        </>
      )}
  
    </Box>
  );
}